// Single source of truth for how dates render across the app — MM-DD-YY for
// date-only fields (Due Date, Follow-up Date, Payment Date, ...) and
// MM-DD-YY : HH:MM AM/PM for timestamps (Requested At, Last Updated, ...).
// Every page should import from here instead of hand-rolling its own
// toLocaleString()/toLocaleDateString() formatter, so a format change only
// ever has to happen in one place.

const pad2 = (value: number): string => String(value).padStart(2, '0');

const toValidDate = (value: unknown): Date | null => {
  if (!value) {
    return null;
  }
  const parsed = value instanceof Date ? value : new Date(value as string);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

// Unparseable-but-non-empty input (e.g. a pre-formatted string that isn't a
// real date) is returned as-is rather than swallowed into '-', so bad data
// stays visible instead of silently disappearing.
const fallback = (value: unknown): string => (typeof value === 'string' && value ? value : '-');

/** MM-DD-YY */
export const formatDateOnly = (value: unknown): string => {
  const parsed = toValidDate(value);
  if (!parsed) {
    return fallback(value);
  }
  return `${pad2(parsed.getMonth() + 1)}-${pad2(parsed.getDate())}-${pad2(parsed.getFullYear() % 100)}`;
};

/** MM-DD-YY : HH:MM AM/PM */
export const formatDateTime = (value: unknown): string => {
  const parsed = toValidDate(value);
  if (!parsed) {
    return fallback(value);
  }

  const datePart = formatDateOnly(parsed);
  const minutes = pad2(parsed.getMinutes());
  const meridiem = parsed.getHours() >= 12 ? 'PM' : 'AM';
  const hour12 = parsed.getHours() % 12 || 12;

  return `${datePart} : ${pad2(hour12)}:${minutes} ${meridiem}`;
};
